'use client';

import { ArrowLeft, BookOpen, Users, Sparkles, Heart } from 'lucide-react';
import { useRouter } from 'next/navigation';
import Button from '@/ui/Button';
import AdsterraBanner from '@/components/AdsterraBanner';

export default function AboutPage() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => router.back()}
          className="mb-8 flex items-center gap-2"
        >
          <ArrowLeft size={20} />
          Back
        </Button>

        <div className="mb-12">
          <h1 className="text-5xl sm:text-6xl font-bold text-foreground mb-4">
            About Minifictionverse
          </h1>
          <p className="text-xl text-muted max-w-2xl">
            A fantasy fanfiction platform created by Mini, exploring the art of
            storytelling through multiple character perspectives.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <AdsterraBanner position="top" />

        {/* Creator Section */}
        <section className="mb-16">
          <div className="bg-card-bg border border-border rounded-2xl p-8 sm:p-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Meet the Creator
            </h2>
            <p className="text-lg text-foreground/80 leading-relaxed mb-4">
              Welcome to my world. I&apos;m <strong>Mini</strong>, a fantasy
              fanfiction writer passionate about exploring storytelling through
              different perspectives. Minifictionverse is my creative space where
              I experiment with narrative techniques and character development.
            </p>
            <p className="text-lg text-foreground/80 leading-relaxed">
              All stories on this platform are entirely imaginary and not linked
              to any real incidents. They are works of fiction created for
              entertainment and artistic expression.
            </p>
          </div>
        </section>

        {/* Story Versions Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">
            Explore Different Perspectives
          </h2>
          <p className="text-lg text-foreground/80 leading-relaxed mb-8">
            One of my key experiments is exploring how a single story changes
            when the main lead&apos;s name is altered. Each version reveals
            different emotional depths and narrative possibilities. Check out
            the different story versions to see this effect in action.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Version 1 */}
            <div className="bg-card-bg border border-border rounded-xl p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center
                justify-center mb-4">
                <BookOpen className="text-primary" size={24} />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">
                Multiple Versions
              </h3>
              <p className="text-foreground/70">
                The same story exists in different versions with varying
                character names to explore narrative impact.
              </p>
            </div>

            {/* Version 2 */}
            <div className="bg-card-bg border border-border rounded-xl p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center
                justify-center mb-4">
                <Sparkles className="text-primary" size={24} />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">
                Character Study
              </h3>
              <p className="text-foreground/70">
                Discover how character names influence reader perception and
                emotional connection to the story.
              </p>
            </div>

            {/* Version 3 */}
            <div className="bg-card-bg border border-border rounded-xl p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center
                justify-center mb-4">
                <Heart className="text-primary" size={24} />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">
                Fanfiction Passion
              </h3>
              <p className="text-foreground/70">
                Immerse yourself in fantasy fanfiction crafted with care and
                creative experimentation.
              </p>
            </div>
          </div>
        </section>

        <AdsterraBanner position="middle" />

        {/* Future Vision Section */}
        <section className="bg-card-bg border border-border rounded-2xl p-8
          sm:p-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            My Creative Journey
          </h2>
          <p className="text-lg text-foreground/80 leading-relaxed mb-4">
            I&apos;m currently honing my craft as a fantasy fanfiction writer.
            My stories explore complex character dynamics and emotional narratives
            through the lens of fantasy worlds. As I develop my writing skills,
            I plan to expand beyond current character inspirations and create
            entirely original narratives.
          </p>
          <p className="text-lg text-foreground/80 leading-relaxed mb-4">
            <strong>Important Note:</strong> All stories on Minifictionverse are
            entirely fictional and imaginative works. They are not based on or
            linked to any real incidents or people.
          </p>
          <p className="text-lg text-foreground/80 leading-relaxed">
            Thank you for being part of this creative journey. I hope you enjoy
            exploring the different perspectives and narrative experiments
            featured on this platform.
          </p>
        </section>

        <AdsterraBanner position="bottom" />
      </div>
    </div>
  );
}
